import { useSyncExternalStore } from "react";

/**
 * Why this tab is running stale assets relative to the current deploy.
 *
 * - `version-poll` — `/version.json` reports a newer build than this tab's HTML.
 *   Passive: the user can keep working; the next in-app navigation triggers a full
 *   reload, and StaleAppIndicator offers an optional refresh.
 * - `chunk-load-error` — a dynamic import failed because a deploy removed the
 *   chunk this tab references. Upgrades `version-poll` when both apply; never
 *   downgraded. StaleReloadModal blocks until reload because the action cannot
 *   succeed on stale assets.
 */
export type StaleReason = "version-poll" | "chunk-load-error";

let staleReason: StaleReason | null = null;
const listeners = new Set<() => void>();

export const markAppStale = (reason: StaleReason) => {
  // chunk-load-error means the user already hit a failure, so it may upgrade
  // an existing version-poll state; it is never downgraded.
  if (staleReason === reason || staleReason === "chunk-load-error") return;
  staleReason = reason;
  listeners.forEach((listener) => listener());
};

export const isAppStale = () => staleReason !== null;

const getStaleReason = () => staleReason;

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export const useIsAppStale = () => useSyncExternalStore(subscribe, isAppStale);

export const useStaleReason = () =>
  useSyncExternalStore(subscribe, getStaleReason);
